// GVC Portal API Configuration — the origin comes from config/env.js (P1-B).
import { API_ORIGIN } from './env';
import { getAccessToken, refresh, isV2, isAuthFailure, notifySessionLost, notifyPasswordResetRequired } from '../auth/session';

const API_URL = API_ORIGIN;

export default API_URL;

const isFormData = (body) => typeof FormData !== 'undefined' && body instanceof FormData;

// P2-A (ruling R13): the user-facing text for a non-ok body. express-validator
// answers 400 { errors: [{ msg, … }] } with NO message — before this, every
// validator 400 surfaced as the bare "HTTP 400". First validator message, else
// the server's message, else the status.
const errorMessageFrom = (body, status) => {
  const msg = Array.isArray(body?.errors) && body.errors[0] && body.errors[0].msg;
  return msg || body?.message || `HTTP ${status}`;
};

/**
 * authFetch — fetch with the session's bearer token attached, one silent
 * refresh + retry on an auth-failure 401. Returns the Response (never throws
 * on !ok) so callers that inspect res.status / res.json() keep their logic.
 * Never touches storage: the session module owns the token.
 */
export const authFetch = async (url, options = {}, _retried = false) => {
  const token = await getAccessToken();
  const headers = {
    ...(isFormData(options.body) ? {} : { 'Content-Type': 'application/json' }),
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };
  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    const body = await response.clone().json().catch(() => null);
    if (isAuthFailure(body)) {
      if (isV2() && !_retried) {
        try { await refresh(); } catch (e) { return response; } // refresh 401 → session lost (emitted inside)
        return authFetch(url, options, true);
      }
      if (!isV2()) notifySessionLost({ code: 'V1_TOKEN_INVALID', message: body?.message });
    }
  }

  // P2-A (R6): a forced-reset token was used outside its allowlist — the app
  // must show the "Set a new password" view. Any route, any caller.
  if (response.status === 403) {
    const body = await response.clone().json().catch(() => null);
    if (body?.code === 'PASSWORD_RESET_REQUIRED') notifyPasswordResetRequired();
  }
  return response;
};

// API helper with authentication (throws on !ok, returns parsed JSON)
export const apiRequest = async (endpoint, options = {}) => {
  const response = await authFetch(`${API_URL}${endpoint}`, options);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(errorMessageFrom(error, response.status));
  }

  return response.json();
};

// API upload helper for FormData (no Content-Type - browser sets multipart boundary)
export const apiUpload = async (endpoint, formData) => {
  const response = await authFetch(`${API_URL}${endpoint}`, { method: 'POST', body: formData });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Upload failed' }));
    throw new Error(errorMessageFrom(error, response.status));
  }

  return response.json();
};

// API methods
export const api = {
  get: (endpoint) => apiRequest(endpoint),
  post: (endpoint, data) => apiRequest(endpoint, { method: 'POST', body: JSON.stringify(data) }),
  put: (endpoint, data) => apiRequest(endpoint, { method: 'PUT', body: JSON.stringify(data) }),
  patch: (endpoint, data) => apiRequest(endpoint, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (endpoint) => apiRequest(endpoint, { method: 'DELETE' }),
  upload: (endpoint, formData) => apiUpload(endpoint, formData),
};
